/**
 * Имя подписки по фильтрам (T11.7).
 *
 * ## Почему имя предлагается, а не спрашивается
 *
 * Пустое поле «название» — лишний шаг ровно там, где человек уже принял
 * решение. Он только что подобрал фильтр и хочет получать по нему
 * находки; заставлять его перед этим что-то придумывать значит менять
 * готовое согласие на задачу. Поле остаётся — но заполненным, и человек
 * либо правит его, либо не замечает.
 *
 * ## Имя описывает, а не нумерует
 *
 * «Поиск 1» и «Поиск 2» одинаковы на вид, и через месяц с тремя
 * подписками список перестаёт читаться. «BMW X5, от 2018, до 4 млн ₽»
 * узнаётся взглядом — а именно так список и просматривают.
 *
 * ## Округление до «млн» и «тыс.» намеренно
 *
 * Точное «до 3 450 000 ₽» в имени занимает место, ничего не добавляя:
 * точная граница лежит в самих фильтрах, а имя нужно, чтобы отличить
 * одну подписку от другой.
 */

import type { Filters } from './filters';

/** Предел имени в договоре и в базе. */
export const NAME_LIMIT = 100;

/** Имя, когда описывать нечего: фильтров нет вовсе. */
export const FALLBACK_NAME = 'Все объявления';

/** Что известно о выбранном автомобиле из справочников. */
export interface Chosen {
  readonly brand?: string | undefined;
  readonly model?: string | undefined;
  readonly generation?: string | undefined;
}

/**
 * Цена коротко: «3 млн ₽», «450 тыс. ₽».
 *
 * Дробная часть остаётся только когда она значит: 2,5 млн — это не 2 и
 * не 3, а «2,0 млн» — то же самое, что «2 млн», лишь длиннее.
 */
function money(minor: number): string {
  const rubles = Math.round(minor / 100);

  if (rubles >= 1_000_000) {
    const millions = Math.round(rubles / 100_000) / 10;

    return `${String(millions).replace('.', ',')} млн ₽`;
  }

  if (rubles >= 1000) return `${String(Math.round(rubles / 1000))} тыс. ₽`;

  return `${String(rubles)} ₽`;
}

function priceOf(filters: Filters): string | null {
  const from = filters.price_min_minor;
  const to = filters.price_max_minor;

  if (from !== undefined && to !== undefined) return `${money(from)} — ${money(to)}`;
  if (to !== undefined) return `до ${money(to)}`;
  if (from !== undefined) return `от ${money(from)}`;

  return null;
}

function yearOf(filters: Filters): string | null {
  const from = filters.year_min;
  const to = filters.year_max;

  if (from !== undefined && to !== undefined) return `${String(from)}–${String(to)}`;
  if (to !== undefined) return `по ${String(to)}`;
  if (from !== undefined) return `от ${String(from)}`;

  return null;
}

/**
 * Обрезка последней части, когда не помещается даже она одна.
 *
 * По пробелу, а не по знаку: «Mercedes-Benz G-Класс AMG G 6» выглядит
 * поломкой. Хвостовая пунктуация снимается отдельно — обрыв на «1 млн ₽
 * —» читается как незаконченная мысль, а не как сокращение.
 */
function shorten(part: string): string {
  const cut = part.slice(0, NAME_LIMIT);
  const space = cut.lastIndexOf(' ');

  return (space > 0 ? cut.slice(0, space) : cut).replace(/[\s,;:—–-]+$/u, '');
}

/**
 * Собирает имя из того, что задано.
 *
 * Порядок частей — порядок узнавания: сперва что за машина, затем годы,
 * затем деньги.
 *
 * Не помещается — отбрасывается целая часть, а не хвост строки. Имя
 * состоит из законченных кусков, и «BMW X5, от 2018» остаётся верным,
 * тогда как «BMW X5, от 2018, 1 млн ₽ —» обещает продолжение, которого
 * нет.
 */
export function subscriptionName(filters: Filters, chosen: Chosen = {}): string {
  const car = [chosen.brand, chosen.model, chosen.generation]
    .filter((one): one is string => one !== undefined && one !== '')
    .join(' ');

  const parts = [car === '' ? null : car, yearOf(filters), priceOf(filters)].filter(
    (one): one is string => one !== null,
  );

  if (parts.length === 0) return FALLBACK_NAME;

  const kept = [...parts];

  while (kept.length > 1 && kept.join(', ').length > NAME_LIMIT) kept.pop();

  const name = kept.join(', ');

  return name.length <= NAME_LIMIT ? name : shorten(name);
}
